<template>
  <div class="sales-history-wrapper m-left-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Executive Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Page Title & Identity -->
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-receipt-text-check-outline</v-icon>
          </div>
          <div>
            <div class="page-title">SALES REPORT</div>
            <div class="page-subtitle">Comprehensive report of retail sales, item totals, deductions &amp; settlements</div>
          </div>
        </div>

        <!-- Right: Action Bar (Search + Refresh + Quick Shortcut) -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <div class="search-input-wrap">
            <v-text-field
              v-model="keyword"
              clearable
              background-color="white"
              color="#9a6b27"
              hide-details
              placeholder="Search by bill no, customer, mobile, staff..."
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              class="search-input"
            ></v-text-field>
          </div>

          <v-btn
            icon
            small
            title="Refresh Report"
            class="refresh-icon-btn"
            @click="getData()"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            to="/EstimatePage"
          >
            <v-icon left small>mdi-plus</v-icon>
            New Estimate
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 4 KPI Overview Metric Cards - Standard Sales Metrics -->
    <div class="kpi-grid mb-4">
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL INVOICES</div>
          <div class="kpi-value">{{ totalInvoicesCount }}</div>
          <div class="kpi-hint">Finalized retail bills</div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="20" color="#2563eb">mdi-receipt-text-outline</v-icon>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">GROSS SALES VALUE</div>
          <div class="kpi-value">₹ {{ formatIndianNumber(totalGrossSalesSum) }}</div>
          <div class="kpi-hint">Total value of items sold</div>
        </div>
        <div class="kpi-icon-pill" style="background: #f0fdf4; color: #16a34a;">
          <v-icon size="20" color="#16a34a">mdi-cash-multiple</v-icon>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">GOLD WEIGHT SOLD</div>
          <div class="kpi-value">{{ formatWeight(totalNetWeightSum) }} <span class="kpi-unit">gm</span></div>
          <div class="kpi-hint">Cumulative net gold sold</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #ca8a04;">
          <v-icon size="20" color="#ca8a04">mdi-gold</v-icon>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">NET BILLED REVENUE</div>
          <div class="kpi-value">₹ {{ formatIndianNumber(totalRevenueSum) }}</div>
          <div class="kpi-hint">Final billed invoice revenue</div>
        </div>
        <div class="kpi-icon-pill" style="background: #f8fafc; color: #0f172a; border: 1px solid #e2e8f0;">
          <v-icon size="20" color="#0f172a">mdi-wallet-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- Master Data Table Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <!-- Data Table View -->
      <div v-if="dataList && dataList.length > 0">
        <div class="table-responsive-wrapper">
          <v-data-table
            :headers="headers"
            :items="dataList"
            :single-expand="singleExpand"
            :expanded.sync="expanded"
            item-key="_id._id"
            show-expand
            hide-default-footer
            :items-per-page="itemsPerPage"
            class="pro-sales-table elevation-0"
          >
            <!-- Bill Number & Status Column -->
            <template v-slot:item.billNo="{ item }">
              <div class="d-flex flex-column align-start py-1">
                <div class="d-flex align-center">
                  <router-link
                    :to="getLink(item)"
                    class="code-pill text-decoration-none hover-pill"
                    title="Click to view tax invoice"
                  >
                    #{{ (item._id && item._id.billPrefix ? item._id.billPrefix : '') + (item._id ? item._id.billNo : '') }}
                  </router-link>
                </div>
              <div class="d-flex align-center mt-1" style="gap: 4px;">
                <span
                  :class="getPaymentStatusBadgeClass(item)"
                  class="status-pill-small"
                >
                  {{ getPaymentStatusText(item) }}
                </span>
                <span class="staff-tag" v-if="item._id && item._id.staffCode" :title="'Handled by ' + item._id.staffCode">
                  {{ item._id.staffCode }}
                </span>
              </div>
            </div>
          </template>

          <!-- Date Column -->
          <template v-slot:item.create_date="{ item }">
            <div class="d-flex align-center date-cell">
              <v-icon size="14" color="#94a3b8" class="mr-1">mdi-calendar-blank-outline</v-icon>
              <span>{{ formatDate(item._id ? (item._id.saleDate || item._id.create_date) : null) }}</span>
            </div>
          </template>

          <!-- Customer Details Column -->
          <template v-slot:item.customer="{ item }">
            <div class="d-flex align-center py-1">
              <div class="customer-avatar mr-2">
                {{ getInitials(item._id && item._id.customerName ? item._id.customerName : 'C') }}
              </div>
              <div>
                <div class="customer-name-text">
                  {{ (item._id && item._id.customerName) ? item._id.customerName : 'Walk-in Customer' }}
                </div>
                <div class="customer-phone-text" v-if="item._id && item._id.mobileNumber">
                  <v-icon size="12" color="#94a3b8" class="mr-1">mdi-phone-outline</v-icon>
                  {{ item._id.mobileNumber }}
                </div>
              </div>
            </div>
          </template>

          <!-- Sold Weight Column -->
          <template v-slot:item.totalNetWeight="{ item }">
            <div class="weight-cell text-right">
              <div class="font-weight-bold text-slate-900">
                {{ formatWeight(item._id ? item._id.totalNetWeight : 0) }} <span class="unit-text">gm</span>
              </div>
              <div class="item-count-subtext">
                {{ item.items ? item.items.length : 0 }} {{ (item.items && item.items.length === 1) ? 'item' : 'items' }}
              </div>
            </div>
          </template>

          <!-- Gross Items Total Column -->
          <template v-slot:item.grossTotal="{ item }">
            <div class="text-right">
              <div class="font-weight-bold text-slate-900">
                ₹ {{ formatIndianNumber(getItemGrossTotal(item)) }}
              </div>
              <div v-if="getItemGrossTotal(item) === 0 && item._id && item._id.oldGoldAmount > 0" class="exchange-tag">
                Exchange Voucher
              </div>
            </div>
          </template>

          <!-- Deductions & Discounts Column -->
          <template v-slot:item.deductions="{ item }">
            <div class="text-right d-flex flex-column align-end py-1" style="gap: 3px;">
              <span v-if="item._id && item._id.billDiscount > 0" class="deduct-chip disc">
                Disc: -₹ {{ formatIndianNumber(item._id.billDiscount) }}
              </span>
              <span v-if="item._id && item._id.oldGoldAmount > 0" class="deduct-chip old-gold">
                Old Gold: -₹ {{ formatIndianNumber(item._id.oldGoldAmount) }}
              </span>
              <span v-if="item._id && item._id.returnAmount > 0" class="deduct-chip return">
                Return: -₹ {{ formatIndianNumber(item._id.returnAmount) }}
              </span>
              <span v-if="!hasDeductions(item)" class="text-muted">—</span>
            </div>
          </template>

          <!-- Tax / GST Column -->
          <template v-slot:item.taxType="{ item }">
            <div class="d-flex flex-column align-center justify-center">
              <span :class="item._id && item._id.taxType ? 'tax-chip-gst' : 'tax-chip-demo'">
                {{ item._id && item._id.taxType ? ('GST (' + getGstPercent(item) + '%)') : 'Demo / Non-Tax' }}
              </span>
              <span v-if="item._id && item._id.taxType && getGstAmount(item) > 0" class="tax-amt-subtext mt-1">
                ₹ {{ formatIndianNumber(getGstAmount(item)) }}
              </span>
            </div>
          </template>

          <!-- Net Bill Amount Column -->
          <template v-slot:item.netAmount="{ item }">
            <div class="text-right">
              <div class="total-amount-val">
                ₹ {{ formatIndianNumber(getNetBillAmount(item)) }}
              </div>
            </div>
          </template>

          <!-- Settlement / Payment Status Column -->
          <template v-slot:item.settlement="{ item }">
            <div class="text-right d-flex flex-column align-end">
              <div class="font-weight-bold" style="font-size: 13px; color: #0f172a;">
                ₹ {{ formatIndianNumber(getPaidAmount(item)) }}
              </div>
              <div v-if="getPendingAmount(item) > 0" class="status-chip-due mt-1">
                Due: ₹ {{ formatIndianNumber(getPendingAmount(item)) }}
              </div>
              <div v-else class="status-chip-settled mt-1">
                Settled
              </div>
            </div>
          </template>

          <!-- Action Column: View Button -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center justify-center">
              <v-btn
                small
                outlined
                color="#9a6b27"
                class="view-action-btn text-capitalize"
                :to="getLink(item)"
                title="View Tax Invoice & Breakdown"
              >
                <v-icon x-small left color="#9a6b27">mdi-eye-outline</v-icon>
                View
              </v-btn>
            </div>
          </template>

          <!-- Expandable Line Items & Complete Sale Breakdown -->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0 expanded-wrapper-cell">
              <div class="nested-items-box">
                <!-- Nested Panel Header -->
                <div class="nested-header d-flex flex-wrap align-center justify-space-between px-4 py-3">
                  <div class="d-flex align-center">
                    <v-icon small color="#0f172a" class="mr-2">mdi-package-variant-closed</v-icon>
                    <span class="nested-title">
                      Purchased Line Items &amp; Sale Details
                      <span class="item-count-badge ml-2">{{ item.items ? item.items.length : 0 }} items</span>
                    </span>
                  </div>
                  <router-link :to="getLink(item)" class="text-decoration-none">
                    <v-btn small text color="#9a6b27" class="font-weight-bold">
                      <v-icon small left>mdi-printer-outline</v-icon>
                      Print Tax Invoice
                    </v-btn>
                  </router-link>
                </div>

                <!-- Sub-table of Items (if items exist) -->
                <div class="nested-table-wrap" v-if="item.items && item.items.length > 0">
                  <v-simple-table dense class="nested-sub-table m-stack-table m-stack-grid">
                    <thead>
                      <tr>
                        <th class="text-left" style="width: 50px;">#</th>
                        <th class="text-left" style="min-width: 160px;">Item / Design</th>
                        <th class="text-center" style="width: 70px;">Qty</th>
                        <th class="text-right" style="width: 120px;">Item Wt (gm)</th>
                        <th class="text-right" style="width: 120px;">Net Wt (gm)</th>
                        <th class="text-right" style="width: 130px;">Making Chg (₹)</th>
                        <th class="text-right" style="width: 120px;">Stone Price (₹)</th>
                        <th class="text-right" style="width: 140px;">Line Total (₹)</th>
                        <th class="text-center" style="width: 90px;">Tax Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(val, idx) in item.items" :key="idx" class="nested-row">
                        <td data-label="#" class="text-muted font-weight-medium">{{ idx + 1 }}</td>
                        <td data-label="Item / Design">
                          <div class="d-flex align-center">
                            <span class="font-weight-bold text-slate-800" v-if="val.itemId && val.itemId.name">
                              {{ val.itemId.name }}
                            </span>
                            <span class="sub-code-pill ml-2" v-if="val.designId && val.designId.code">
                              {{ val.designId.code }}
                            </span>
                            <span v-if="!val.itemId && !val.designId" class="text-muted">-</span>
                          </div>
                        </td>
                        <td data-label="Qty" class="text-center font-weight-semibold">{{ val.quantity || 1 }}</td>
                        <td data-label="Item Wt (gm)" class="text-right font-weight-medium">{{ formatWeight(val.itemPerWeight || val.grossWeight) }}</td>
                        <td data-label="Net Wt (gm)" class="text-right font-weight-bold text-slate-900">{{ formatWeight(val.netWeight) }}</td>
                        <td data-label="Making Chg (₹)" class="text-right">
                          <span v-if="val.makingCharge > 0">₹ {{ formatIndianNumber(val.makingCharge) }}</span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td data-label="Stone Price (₹)" class="text-right">
                          <span v-if="val.stonePrice > 0">₹ {{ formatIndianNumber(val.stonePrice) }}</span>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td data-label="Line Total (₹)" class="text-right font-weight-bold" style="color: #0f172a;">
                          ₹ {{ formatIndianNumber(val.taxType ? val.amountWithGst : val.amountWithoutGst) }}
                        </td>
                        <td data-label="Tax Type" class="text-center">
                          <span :class="val.taxType ? 'sub-tax-gst' : 'sub-tax-demo'">
                            {{ val.taxType ? ('GST ' + (((Number(val.cgst) || 0) + (Number(val.sgst) || 0)) || 3) + '%') : 'Demo' }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>

                <!-- Pure Exchange Notification (if no retail items) -->
                <div v-else class="pa-4 bg-slate-50 text-center" style="background: #f8fafc; border-bottom: 1px solid #e2e8f0;">
                  <v-icon color="#ea580c" size="24" class="mb-1">mdi-swap-horizontal-bold</v-icon>
                  <div style="font-size: 13px; font-weight: 600; color: #0f172a;">Old Gold Exchange / Purchase Voucher</div>
                  <div style="font-size: 12px; color: #64748b; margin-top: 2px;">
                    This invoice is a direct Old Gold exchange transaction with no retail ornament items. Total exchange credit:
                    <b style="color: #ea580c;">₹ {{ formatIndianNumber(item._id ? item._id.oldGoldAmount : 0) }}</b>
                  </div>
                </div>

                <!-- Complete Sale Reconciliation Card (4 Metric Columns) -->
                <div class="nested-summary-card pa-4">
                  <div class="summary-section-label mb-3">INVOICE FINANCIAL SUMMARY &amp; SETTLEMENT</div>
                  <div class="summary-grid">
                    <!-- Col 1: Gross Sale -->
                    <div class="summary-col">
                      <div class="summary-col-label">1. Gross Items Total</div>
                      <div class="summary-col-val text-slate-900">₹ {{ formatIndianNumber(getItemGrossTotal(item)) }}</div>
                      <div class="summary-col-hint">Total ornament value</div>
                    </div>

                    <!-- Col 2: Deductions & Discount -->
                    <div class="summary-col">
                      <div class="summary-col-label">2. Deductions &amp; Discount</div>
                      <div class="d-flex flex-column" style="gap: 2px;">
                        <span v-if="item._id && item._id.billDiscount > 0" class="summary-deduct-val">
                          Disc: -₹ {{ formatIndianNumber(item._id.billDiscount) }}
                        </span>
                        <span v-if="item._id && item._id.oldGoldAmount > 0" class="summary-deduct-val" style="color: #ea580c;">
                          Old Gold: -₹ {{ formatIndianNumber(item._id.oldGoldAmount) }}
                        </span>
                        <span v-if="item._id && item._id.returnAmount > 0" class="summary-deduct-val" style="color: #dc2626;">
                          Return: -₹ {{ formatIndianNumber(item._id.returnAmount) }}
                        </span>
                        <span v-if="!hasDeductions(item)" class="text-muted" style="font-size: 12px;">No deductions</span>
                      </div>
                    </div>

                    <!-- Col 3: Tax & Net Invoice -->
                    <div class="summary-col">
                      <div class="summary-col-label">3. Net Bill Amount</div>
                      <div class="summary-col-val" style="color: #0f172a; font-size: 16px;">
                        ₹ {{ formatIndianNumber(getNetBillAmount(item)) }}
                      </div>
                      <div class="summary-col-hint">
                        {{ item._id && item._id.taxType ? ('Includes GST ' + getGstPercent(item) + '% (₹ ' + formatIndianNumber(getGstAmount(item)) + ')') : 'Demo / Non-GST' }}
                      </div>
                    </div>

                    <!-- Col 4: Settlement -->
                    <div class="summary-col">
                      <div class="summary-col-label">4. Payment &amp; Balance</div>
                      <div class="summary-col-val" style="color: #16a34a;">
                        Paid: ₹ {{ formatIndianNumber(getPaidAmount(item)) }}
                      </div>
                      <div v-if="getPendingAmount(item) > 0" style="color: #dc2626; font-size: 12px; font-weight: 700;">
                        Balance Due: ₹ {{ formatIndianNumber(getPendingAmount(item)) }}
                      </div>
                      <div v-else style="color: #16a34a; font-size: 11.5px; font-weight: 600;">
                        Fully Settled
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </template>
        </v-data-table>
        </div>

        <!-- Custom Pagination & Summary Bar -->
        <div class="table-pagination-bar px-4 py-3 d-flex flex-wrap align-center justify-space-between">
          <div class="pagination-info">
            Showing
            <span class="font-weight-bold text-slate-800">{{ showingFrom }}</span>
            to
            <span class="font-weight-bold text-slate-800">{{ showingTo }}</span>
            of
            <span class="font-weight-bold text-slate-800">{{ totalLength }}</span>
            invoices
          </div>

          <v-pagination
            v-if="pageLength > 1"
            v-model="page"
            :length="pageLength"
            :total-visible="7"
            circle
            color="#9a6b27"
            class="custom-pagination"
          ></v-pagination>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap text-center">
        <div class="empty-icon-circle mb-3">
          <v-icon size="32" color="#94a3b8">mdi-receipt-text-outline</v-icon>
        </div>
        <div class="empty-title">No Sales Invoices Found</div>
        <div class="empty-subtitle">
          {{ keyword ? 'No sales records matched your search query. Try clearing filters.' : 'There are no finalized sales invoices recorded in the system yet.' }}
        </div>
        <div class="mt-4" v-if="keyword">
          <v-btn small outlined color="#0f172a" @click="keyword = ''; getData()">
            <v-icon small left>mdi-filter-remove-outline</v-icon>
            Clear Search
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "SalesPage1",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      page: 1,
      itemsPerPage: 20,
      totalLength: 0,
      pageLength: 1,
      dataList: [],
      keyword: null,
      juwellaryId: localStorage.getItem("juwellaryId"),
      singleExpand: true,
      expanded: [],
      headers: [
        { text: "Bill No & Status", value: "billNo", sortable: false, width: "135px" },
        { text: "Date", value: "create_date", sortable: false, width: "105px" },
        { text: "Customer", value: "customer", sortable: false, minWidth: "150px" },
        { text: "Sold Weight", value: "totalNetWeight", align: "end", sortable: false, width: "105px" },
        { text: "Gross Items (₹)", value: "grossTotal", align: "end", sortable: false, width: "120px" },
        { text: "Deductions & Disc (₹)", value: "deductions", align: "end", sortable: false, width: "130px" },
        { text: "Tax / GST", value: "taxType", align: "center", sortable: false, width: "100px" },
        { text: "Net Bill (₹)", value: "netAmount", align: "end", sortable: false, width: "120px" },
        { text: "Action", value: "actions", align: "center", sortable: false, width: "95px" },
        { text: "", value: "data-table-expand", width: "40px" },
      ],
    };
  },
  mounted() {
    this.getData();
  },
  watch: {
    keyword() {
      this.page = 1;
      this.getData();
    },
    page() {
      this.getData();
    },
  },
  computed: {
    getLink() {
      return (item) => `/billDetail?id=${item && item._id ? item._id._id : ""}`;
    },
    totalInvoicesCount() {
      return this.totalLength || (this.dataList ? this.dataList.length : 0);
    },
    totalGrossSalesSum() {
      if (!this.dataList || !this.dataList.length) return 0;
      return this.dataList.reduce((acc, curr) => {
        return acc + this.getItemGrossTotal(curr);
      }, 0);
    },
    totalNetWeightSum() {
      if (!this.dataList || !this.dataList.length) return 0;
      return this.dataList.reduce((acc, curr) => {
        const wt = curr._id && curr._id.totalNetWeight ? Number(curr._id.totalNetWeight) : 0;
        return acc + (isNaN(wt) ? 0 : wt);
      }, 0);
    },
    totalRevenueSum() {
      if (!this.dataList || !this.dataList.length) return 0;
      return this.dataList.reduce((acc, curr) => {
        return acc + this.getNetBillAmount(curr);
      }, 0);
    },
    showingFrom() {
      if (!this.totalLength || this.totalLength === 0) return 0;
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    showingTo() {
      if (!this.totalLength) return this.dataList ? this.dataList.length : 0;
      return Math.min(this.page * this.itemsPerPage, this.totalLength);
    },
  },
  methods: {
    getData() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/sales/alllist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          keyword: this.keyword || undefined,
          page: this.page,
          count: this.itemsPerPage,
          jewelleryId: this.juwellaryId,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.dataList = response.data.data || [];
            this.totalLength = response.data.totalLength || this.dataList.length;
            this.pageLength = Math.max(1, Math.ceil(this.totalLength / this.itemsPerPage));
          } else {
            this.showsnackbar = true;
            this.msg = response.data ? response.data.msg : "Failed to load sales";
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Error loading sales history:", err);
        });
    },
    getItemGrossTotal(item) {
      if (!item || !item._id) return 0;
      if (item._id.saleTotal !== undefined && item._id.saleTotal !== null && Number(item._id.saleTotal) > 0) {
        return Number(item._id.saleTotal);
      }
      if (item.items && item.items.length > 0) {
        return item.items.reduce((acc, curr) => {
          const lineAmt = curr.taxType ? curr.amountWithGst : curr.amountWithoutGst;
          return acc + (Number(lineAmt) || 0);
        }, 0);
      }
      return 0;
    },
    getNetBillAmount(item) {
      if (!item || !item._id) return 0;
      // Not item._id.payableAmount - that field gets overwritten with just
      // the last installment's tendered amount on every partial-payment
      // settlement, so it collapses to a tiny number on any bill paid in
      // more than one installment. billNetAmount (sale total minus old-
      // metal/return) is recomputed identically on every settlement from
      // figures that don't change with payment, so net of the bill discount
      // it reliably equals the full bill total.
      if (item._id.billNetAmount !== undefined && item._id.billNetAmount !== null) {
        return Number(item._id.billNetAmount || 0) - Number(item._id.billDiscount || 0);
      }
      if (item._id.totalAmount !== undefined && item._id.totalAmount !== null && Number(item._id.totalAmount) > 0) {
        return Number(item._id.totalAmount);
      }
      return Number(item._id.totalAmount || 0);
    },
    getPaidAmount(item) {
      if (!item || !item._id) return 0;
      if (item._id.billPaidAmount !== undefined && item._id.billPaidAmount !== null) {
        return Number(item._id.billPaidAmount);
      }
      return this.getNetBillAmount(item);
    },
    getPendingAmount(item) {
      if (!item || !item._id) return 0;
      return Number(item._id.pendingAmount || 0);
    },
    getGstAmount(item) {
      if (!item || !item._id) return 0;
      if (item.items && item.items.length > 0) {
        return item.items.reduce((acc, curr) => {
          if (!curr.taxType) return acc;
          const cgst = Number(curr.cgstAmount) || 0;
          const sgst = Number(curr.sgstAmount) || 0;
          if (cgst > 0 || sgst > 0) return acc + cgst + sgst;
          const withGst = Number(curr.amountWithGst) || 0;
          const withoutGst = Number(curr.amountWithoutGst) || 0;
          return acc + Math.max(0, withGst - withoutGst);
        }, 0);
      }
      if (item._id.gst !== undefined && item._id.gst !== null && Number(item._id.gst) > 0) {
        return Number(item._id.gst);
      }
      if (item._id.taxType && this.getNetBillAmount(item) > 0) {
        const total = this.getNetBillAmount(item);
        return (total * 0.03) / 1.03;
      }
      return 0;
    },
    getGstPercent(item) {
      if (!item) return 3;
      const taxedItem = item.items && item.items.find((curr) => curr.taxType && (Number(curr.cgst) > 0 || Number(curr.sgst) > 0));
      if (taxedItem) {
        return Number(taxedItem.cgst || 0) + Number(taxedItem.sgst || 0);
      }
      return 3;
    },
    getTotalDeductions(item) {
      if (!item || !item._id) return 0;
      const og = Number(item._id.oldGoldAmount || 0);
      const ret = Number(item._id.returnAmount || 0);
      const disc = Number(item._id.billDiscount || 0);
      return og + ret + disc;
    },
    hasDeductions(item) {
      return this.getTotalDeductions(item) > 0;
    },
    getPaymentStatusText(item) {
      if (!item || !item._id) return "PAID";
      if (item._id.pendingAmount && Number(item._id.pendingAmount) > 0) {
        return "PARTIAL";
      }
      return "PAID";
    },
    getPaymentStatusBadgeClass(item) {
      if (!item || !item._id) return "status-badge-paid";
      if (item._id.pendingAmount && Number(item._id.pendingAmount) > 0) {
        return "status-badge-partial";
      }
      return "status-badge-paid";
    },
    formatDate(dateVal) {
      if (!dateVal) return "-";
      try {
        const dt = new Date(dateVal);
        if (isNaN(dt.getTime())) return "-";
        const day = dt.getDate();
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[dt.getMonth()];
        const year = dt.getFullYear();
        return `${day} ${month} ${year}`;
      } catch (e) {
        return "-";
      }
    },
    formatIndianNumber(amount) {
      if (amount === undefined || amount === null || isNaN(amount)) return "0.00";
      const val = Number(amount);
      return val.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatWeight(wt) {
      if (wt === undefined || wt === null || isNaN(wt)) return "0.000";
      const val = Number(wt);
      return val.toFixed(3);
    },
    getInitials(name) {
      if (!name) return "C";
      const trimmed = name.trim();
      if (!trimmed) return "C";
      const parts = trimmed.split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return trimmed.substring(0, 2).toUpperCase();
    },
  },
};
</script>

<style scoped>
.sales-history-wrapper {
  padding: 4px 0 40px 0;
  width: 100%;
  margin: 0 auto;
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
}

/* Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.header-icon-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.search-input-wrap {
  width: 330px;
}

@media (max-width: 960px) {
  .search-input-wrap {
    width: 100%;
  }
}

.search-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 8px !important;
}

.search-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.search-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
  border-width: 1.5px !important;
}

.refresh-icon-btn {
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  background: #ffffff !important;
  height: 38px !important;
  width: 38px !important;
}

.refresh-icon-btn:hover {
  background: #f8fafc !important;
}

.btn-primary {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.2s ease !important;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

/* 4 KPI Metrics Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 19px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 3px 0;
  line-height: 1.2;
}

.kpi-unit {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
}

.kpi-icon-pill {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Master Data Table Card */
.content-table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.pro-sales-table :deep(table) {
  width: 100%;
  border-collapse: collapse;
}

.pro-sales-table :deep(th) {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 14px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  white-space: nowrap;
}

.pro-sales-table :deep(td) {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 12px 14px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.pro-sales-table :deep(tr:hover) {
  background-color: #f8fafc !important;
}

.code-pill {
  font-family: monospace;
  font-weight: 700;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  color: #0f172a;
  letter-spacing: 0.3px;
}

.status-pill-small {
  font-size: 9.5px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 4px;
  letter-spacing: 0.3px;
}

.status-badge-paid {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.status-badge-partial {
  background: #ffedd5;
  color: #c2410c;
  border: 1px solid #fed7aa;
}

.staff-tag {
  font-size: 9.5px;
  font-weight: 600;
  background: #f1f5f9;
  color: #64748b;
  padding: 1px 5px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.date-cell {
  font-size: 12.5px;
  font-weight: 500;
  color: #475569;
}

.customer-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.customer-name-text {
  font-weight: 600;
  color: #0f172a;
  font-size: 13px;
}

.customer-phone-text {
  font-size: 11.5px;
  color: #64748b;
  display: flex;
  align-items: center;
  margin-top: 1px;
}

.weight-cell {
  font-size: 13px;
}

.unit-text {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.item-count-subtext {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 1px;
}

.exchange-tag {
  font-size: 10.5px;
  color: #ea580c;
  font-weight: 600;
}

.deduct-chip {
  font-size: 10.5px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  display: inline-block;
}

.deduct-chip.disc {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.deduct-chip.old-gold {
  background: #fff7ed;
  color: #ea580c;
  border: 1px solid #ffedd5;
}

.deduct-chip.return {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fee2e2;
}

.tax-chip-gst {
  font-size: 10px;
  font-weight: 600;
  background: #eff6ff;
  color: #2563eb;
  padding: 2px 7px;
  border-radius: 4px;
  border: 1px solid #dbeafe;
}

.tax-chip-demo {
  font-size: 10px;
  font-weight: 600;
  background: #f8fafc;
  color: #64748b;
  padding: 2px 7px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.tax-amt-subtext {
  font-size: 11px;
  color: #2563eb;
  font-weight: 600;
}

.total-amount-val {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.status-chip-settled {
  font-size: 10px;
  font-weight: 600;
  color: #16a34a;
  background: #f0fdf4;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid #bbf7d0;
}

.status-chip-due {
  font-size: 10px;
  font-weight: 700;
  color: #dc2626;
  background: #fef2f2;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid #fecaca;
}

.view-bill-btn {
  border-radius: 6px !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  text-transform: capitalize !important;
  border-color: #cbd5e1 !important;
}

.view-bill-btn:hover {
  background: #f8fafc !important;
  border-color: #94a3b8 !important;
}

/* Expanded Nested Details */
.expanded-wrapper-cell {
  background: #f8fafc !important;
  padding: 12px 16px !important;
}

.nested-items-box {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.nested-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.nested-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.item-count-badge {
  font-size: 11px;
  background: #e2e8f0;
  color: #475569;
  padding: 2px 7px;
  border-radius: 12px;
  font-weight: 600;
}

.nested-table-wrap {
  overflow-x: auto;
}

.nested-sub-table th {
  background: #ffffff !important;
  color: #64748b !important;
  font-size: 10.5px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 8px 12px !important;
}

.nested-sub-table td {
  font-size: 12px !important;
  padding: 8px 12px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.nested-row:hover {
  background: #fafafa !important;
}

.sub-code-pill {
  font-family: monospace;
  font-size: 11px;
  background: #f1f5f9;
  padding: 1px 5px;
  border-radius: 4px;
  color: #475569;
}

.sub-tax-gst {
  font-size: 10px;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
}

.sub-tax-demo {
  font-size: 10px;
  background: #f1f5f9;
  color: #64748b;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
}

/* Nested Financial Summary */
.nested-summary-card {
  background: #fbfcfe;
  border-top: 1px solid #e2e8f0;
}

.summary-section-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 960px) {
  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 500px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}

.summary-col {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px 12px;
}

.summary-col-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
}

.summary-col-val {
  font-size: 14px;
  font-weight: 700;
}

.summary-col-hint {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.summary-deduct-val {
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
}

/* Pagination Bar */
.table-pagination-bar {
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.pagination-info {
  font-size: 12.5px;
  color: #64748b;
}

.custom-pagination :deep(.v-pagination__item) {
  font-size: 12px !important;
  min-width: 28px !important;
  height: 28px !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
}

.custom-pagination :deep(.v-pagination__navigation) {
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  width: 28px !important;
  height: 28px !important;
}

/* Empty State */
.empty-state-wrap {
  padding: 60px 20px;
}

.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

.text-muted {
  color: #94a3b8;
}

.table-responsive-wrapper {
  width: 100%;
  overflow-x: auto;
}

.view-action-btn {
  border-color: #d4af37 !important;
  color: #9a6b27 !important;
  font-weight: 600 !important;
  font-size: 11.5px !important;
  height: 28px !important;
  padding: 0 10px !important;
  border-radius: 6px !important;
  background: #fffdfa !important;
  transition: all 0.2s ease !important;
}

.view-action-btn:hover {
  background: #fefce8 !important;
  border-color: #9a6b27 !important;
  box-shadow: 0 2px 6px rgba(154, 106, 44, 0.18) !important;
}

.hover-pill {
  transition: all 0.2s ease;
  cursor: pointer;
}

.hover-pill:hover {
  background: #e2e8f0 !important;
  color: #9a6b27 !important;
  border-color: #cbd5e1 !important;
}
</style>
